import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

/*
 * Zustand einer Filterleiste.
 *
 * Haelt die Auswahl je Feld, spiegelt sie in die Adresszeile und merkt sie
 * fuer den naechsten Besuch. Beides hat einen anderen Zweck:
 *
 * - Adresszeile: eine gefilterte Ansicht ist neu ladbar und weitergebbar, der
 *   Zurueck-Knopf des Browsers funktioniert. Dort stehen nur die Werte.
 * - Merken (localStorage): beim naechsten Aufruf derselben Ansicht steht der
 *   letzte Filter wieder da. Dort stehen Wert *und* Beschriftung, denn zu einer
 *   Schul-Nummer aus der Adresszeile kennt niemand den Namen, ohne sie erst
 *   nachzuladen.
 *
 * Die gemerkten Filter haengen am Browser, nicht am Konto — deshalb raeumt
 * `clearStoredFilters()` sie beim Abmelden weg (siehe shared/helpers.js).
 */

export const FILTER_STORAGE_PREFIX = 'junior.filter.'

const PARAM_PREFIX = 'f_'
const SEPARATOR = ','

const castValue = (raw, valueType) => {
	if (valueType === 'boolean') return raw === 'true'
	if (valueType === 'number') return Number(raw)
	return raw
}

/**
 * Der gemerkte Stand — oder `null`, wenn diese Ansicht noch nie gefiltert
 * wurde.
 *
 * Der Unterschied zwischen "nichts gemerkt" und "gemerkt, aber leer" traegt:
 * Nur beim allerersten Besuch darf eine Voreinstellung greifen (siehe
 * `defaults`). Wer den Filter einmal geleert hat, will ihn leer vorfinden.
 */
const readStorage = storageKey => {
	try {
		const raw = window.localStorage.getItem(FILTER_STORAGE_PREFIX + storageKey)
		if (raw === null) return null
		const parsed = JSON.parse(raw)
		return parsed && typeof parsed === 'object' ? parsed : null
	} catch (error) {
		return null
	}
}

const writeStorage = (storageKey, values) => {
	try {
		window.localStorage.setItem(FILTER_STORAGE_PREFIX + storageKey, JSON.stringify(values))
	} catch (error) {
		// Speicher voll oder gesperrt: das Filtern selbst funktioniert weiter.
	}
}

/** Alle gemerkten Filter verwerfen — beim Abmelden. */
export const clearStoredFilters = () => {
	try {
		Object.keys(window.localStorage)
			.filter(key => key.startsWith(FILTER_STORAGE_PREFIX))
			.forEach(key => window.localStorage.removeItem(key))
	} catch (error) {
		// s. o.
	}
}

const sameValue = (a, b) => String(a) === String(b)

// Beschriftung zu einem Wert: erst aus den Optionen des Feldes, sonst aus dem
// gemerkten Stand, sonst der Rohwert (besser als eine leere Zeile).
const optionFor = (field, value, stored) => {
	const fromOptions = (field.options || []).find(option => sameValue(option.value, value))
	if (fromOptions) return fromOptions

	const fromStorage = (stored[field.key] || []).find(option => sameValue(option.value, value))
	if (fromStorage) return fromStorage

	return { value, label: String(value) }
}

/*
 * Der Anfangsstand: aus der Adresszeile, sonst aus dem Merker, sonst leer.
 *
 * `hatStand` haelt fest, ob es ueberhaupt einen gab. Ohne einen darf eine
 * Voreinstellung nachruecken, sobald sie feststeht (`defaults`).
 */
const initialState = (fields, search, storageKey) => {
	const params = new URLSearchParams(search)
	const stored = readStorage(storageKey)
	const hasUrlFilters = fields.some(field => params.has(PARAM_PREFIX + field.key))

	const values = fields.reduce((result, field) => {
		if (hasUrlFilters) {
			const raw = params.get(PARAM_PREFIX + field.key) || ''
			result[field.key] = raw
				.split(SEPARATOR)
				.filter(Boolean)
				.map(part => optionFor(field, castValue(part, field.valueType), stored || {}))
		} else {
			result[field.key] = Array.isArray(stored?.[field.key]) ? stored[field.key] : []
		}
		return result
	}, {})

	return { values, hatStand: hasUrlFilters || stored !== null }
}

/**
 * @param {object}   options
 * @param {string}   options.storageKey  Merkzettel dieser Ansicht.
 * @param {Array}    options.fields      Felder der Leiste.
 * @param {object}   [options.defaults]  Voreingestellte Auswahl je Feld, als
 *   Optionen (`{ value, label }`) — z. B. das eigene Bundesland einer
 *   Landesadministration. Sie greift **nur beim allerersten Besuch** dieser
 *   Ansicht und darf jederzeit geaendert oder geleert werden. Der Wert darf
 *   nachtraeglich kommen (das Profil steht erst nach dem Abruf fest), muss aber
 *   stabil sein — sonst wird er bei jedem Rendern neu gesetzt.
 */
export default function useFilters({ storageKey, fields, defaults }) {
	const history = useHistory()
	const [start] = useState(() => initialState(fields, history.location.search, storageKey))
	const [values, setValues] = useState(start.values)

	// Adresszeile und Merker nachziehen. `history.replace` legt keinen neuen
	// Eintrag an — sonst muesste man je Klick einmal zurueck.
	useEffect(() => {
		const params = new URLSearchParams(history.location.search)

		Object.entries(values).forEach(([key, selected]) => {
			if (selected?.length) params.set(PARAM_PREFIX + key, selected.map(option => option.value).join(SEPARATOR))
			else params.delete(PARAM_PREFIX + key)
		})

		const query = params.toString()
		const next = query ? `?${query}` : ''
		if (next !== history.location.search) history.replace({ pathname: history.location.pathname, search: next })

		writeStorage(storageKey, values)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values, storageKey])

	// Beschriftungen nachtragen, sobald die Optionen da sind: Bundesland und
	// Branche kommen aus dem Store, aus der Adresszeile kam vorher nur der Wert.
	useEffect(() => {
		setValues(previous => {
			let changed = false

			const next = Object.entries(previous).reduce((result, [key, selected]) => {
				const field = fields.find(item => item.key === key)
				result[key] = selected.map(option => {
					const known = (field?.options || []).find(item => sameValue(item.value, option.value))
					if (!known || known.label === option.label) return option
					changed = true
					return known
				})
				return result
			}, {})

			return changed ? next : previous
		})
	}, [fields])

	/*
	 * Voreinstellung nachruecken.
	 *
	 * Die Landesadministration soll ihr eigenes Bundesland vorgewaehlt
	 * vorfinden — ihr Profil steht beim ersten Rendern aber noch nicht fest.
	 * Deshalb ein Effekt statt eines Anfangswerts, und deshalb der Merker: Er
	 * laeuft genau einmal, sonst kaeme die Vorauswahl nach jedem Leeren zurueck.
	 */
	const defaultsGesetzt = useRef(false)

	useEffect(() => {
		if (defaultsGesetzt.current) return
		if (!defaults || !Object.keys(defaults).length) return

		defaultsGesetzt.current = true
		// Wer schon einmal gefiltert hat (oder ueber einen Link kommt), bekommt
		// seinen Stand — nicht die Voreinstellung.
		if (start.hatStand) return

		setValues(previous => {
			const next = { ...previous }
			let changed = false

			Object.entries(defaults).forEach(([key, selected]) => {
				if (!selected?.length) return
				if (previous[key]?.length) return
				if (!fields.some(field => field.key === key)) return

				next[key] = selected
				changed = true
			})

			return changed ? next : previous
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaults, start])

	const setField = useCallback((key, selected) => {
		setValues(previous => ({ ...previous, [key]: selected }))
	}, [])

	const clearField = useCallback(key => {
		setValues(previous => ({ ...previous, [key]: [] }))
	}, [])

	const resetAll = useCallback(() => {
		setValues(previous =>
			Object.keys(previous).reduce((result, key) => {
				result[key] = []
				return result
			}, {})
		)
	}, [])

	const count = useMemo(
		() => Object.values(values).reduce((sum, selected) => sum + (selected?.length || 0), 0),
		[values]
	)

	// Form, die das Backend erwartet: nur die Werte, leere Felder fallen weg.
	const query = useMemo(
		() =>
			Object.entries(values).reduce((result, [key, selected]) => {
				if (selected?.length) result[key] = selected.map(option => option.value)
				return result
			}, {}),
		[values]
	)

	return { values, count, query, setField, clearField, resetAll }
}
